import type { Ref } from 'react';
import { flexRender, type HeaderGroup } from '@tanstack/react-table';
import { clsx } from 'clsx';
import { Skeleton } from '../../loading/skeleton';
import { TD } from '../table-body/td/td';
import styles from './table-footer.module.scss';

const LOADING_MORE_ROWS = 10;

const skeletonElement = <Skeleton parentClassName={styles.pendingData} />;

const loadingMoreRows = new Array(LOADING_MORE_ROWS).fill(null).map((_, i) => `loading-row-${i}`);

interface TableFooterProps<TData extends object> {
  groups: HeaderGroup<TData>[];
  isFetchingNextPage: boolean | undefined;
  isShowingData: boolean;
  ref: Ref<HTMLTableSectionElement>;
}

export function TableFooter<TData extends object>({ groups, isFetchingNextPage, isShowingData, ref }: TableFooterProps<TData>) {
  'use no memo';

  const hasFooter = groups.some((group) => group.headers.some((header) => header.column.columnDef.footer));

  return (
    <tfoot className={styles.tableFooter} ref={ref}>
      {isFetchingNextPage
        ? loadingMoreRows.map((key) => (
            <tr className={styles.tr} key={key}>
              {groups.map((group) =>
                group.headers.map((header) => (
                  <TD<TData> header={header} key={header.column.id} showSkeleton>
                    {skeletonElement}
                  </TD>
                )),
              )}
            </tr>
          ))
        : null}

      {hasFooter && isShowingData ? (
        <tr className={clsx(styles.tr, styles.aggregateRow)}>
          {groups.map((group) =>
            group.headers.map((footer) => (
              <TD<TData> header={footer} isAggregateCell key={footer.column.id} showSkeleton={false}>
                {flexRender(footer.column.columnDef.footer, footer.getContext())}
              </TD>
            )),
          )}
        </tr>
      ) : null}
    </tfoot>
  );
}
